/**
 * PublicViewProvider — read-only context for public (no-login) share links.
 *
 * A masked shell renders campaign-detail screens for anonymous visitors
 * hitting a shared link. Every masked component reads `usePublicView()` to
 * know whether it's rendering in public mode, what grant scoped the link
 * (creator vs. page, which tab/pill/pagination it's pinned to), and how to
 * prompt the visitor to log in for the full experience.
 */
import { createContext, useContext, useMemo, type JSX, type ReactNode } from 'react';
import type {
  PublicRosterPage,
  PublicDetailsView,
  PublicShopifyView,
  PublicShopifyQuery,
} from '@discovr/features';
import type { PendingPublicAction } from '@discovr/common';

export interface PublicShareGrantContext {
  kind: 'creator' | 'page';
  username?: string;
  purpose?: string;
  sub?: string;
  tab?: string;
  pill?: string;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  /** Reporting-window preset (Task 12) — present only for a
   *  general-insights/sentiment grant. Rendered as a static, non-interactive
   *  label on the public path; never re-derived client-side. */
  window?: string;
  expiresAt: string;
}

export interface PublicViewValue {
  isPublicView: boolean;
  grant: PublicShareGrantContext | null;
  /**
   * The granted roster page's real data — page-kind grants for a roster tab
   * (Creators, and Confirmed/Scripts/Content/Live-Content in later tasks)
   * only. `null`/absent for a creator-kind grant (script/content/insights/
   * safety), a non-roster page-kind grant (details/reporting/shopify), or
   * outside public mode.
   *
   * There is no authed endpoint a public visitor could call to fetch this —
   * the whole point of the public payload is that it's already fetched
   * up-front by `PublicShareViewPage` and handed down here. The masked
   * roster tab reads this directly instead of calling its normal
   * self-fetching hooks (which it still calls, but with `enabled: false`, so
   * their own pill/loading state stays a permanent, honest skeleton for
   * every campaign-wide aggregate the public payload never carries).
   */
  rosterPage?: PublicRosterPage | null;
  /**
   * The Details tab's public payload — same "already fetched up-front"
   * story as `rosterPage`, and same reason it exists: there is no authed
   * endpoint an anonymous visitor could call. `null`/absent for every grant
   * except a "details" page-kind one. `CampaignDetailsTab.tsx` assembles a
   * partial-`Campaign`-shaped object straight from this and hands it,
   * unmodified, to `CampaignDetailsCards.tsx` — see that DTO's own field
   * table in shareGrants.ts for why field names match `Campaign` 1:1.
   */
  campaignDetails?: PublicDetailsView | null;
  /**
   * The Shopify tab's public payload (Coupons sub-view only — see
   * PublicShopifyView's doc comment for why the tab's OTHER sub-view,
   * Settings, has no public rendering path at all). `null`/absent for every
   * grant except a "shopify" page-kind one.
   */
  shopifyCoupons?: PublicShopifyView | null;
  /**
   * Which page of the Shopify coupons the public payload holds, and how to ask
   * for another. The payload is one fetch of the whole share view, so paging,
   * the live filter and search are re-fetches the page owner makes when this
   * changes. `shopifyPageLoading` is true while the previous page is still what
   * `shopifyCoupons` holds.
   */
  shopifyQuery?: PublicShopifyQuery;
  setShopifyQuery?: (patch: Partial<Omit<PublicShopifyQuery, 'search'>>) => void;
  /** The search box's text, which runs ahead of `shopifyQuery.search` (the applied term). */
  shopifySearchInput?: string;
  setShopifySearchInput?: (value: string) => void;
  shopifyPageLoading?: boolean;
  /** The requested coupons page failed to load; `shopifyCoupons` still holds the last good page. */
  shopifyPageError?: boolean;
  retryShopifyPage?: () => void;
  /**
   * The campaign's platform, sourced from the already-fetched public payload
   * (PublicCampaignContext.platform). Masked components that need it for
   * deliverable classification (isMain/isAddon in
   * packages/types/deliverableRegistry.ts) read it from here instead of the
   * authed campaign-header query, which stays disabled in public mode.
   */
  campaignPlatform?: string;
  /**
   * The short-code + path-tail identifying THIS share link (the same
   * `campCode`/`tail` params `PublicShareViewPage` receives from the route
   * and passes to `fetchPublicShareView`). Carried down through context so a
   * masked component several layers deep (e.g. CommercialPopover, via
   * CreatorsTab) can stamp a `PendingPublicAction` with the link it came
   * from, without threading two more props through every intermediate
   * component. `''` outside public mode / when not yet known — never used
   * there since `requestLogin` only ever needs an action in public mode.
   */
  campCode?: string;
  tail?: string;
  requestLogin: (action?: PendingPublicAction) => void;
  /**
   * True when this public view is rendering for a visitor who IS logged in,
   * but whose session isn't authorized for this campaign (ShortLinkResolverPage's
   * authed pre-flight check found no access, so it fell back here instead of
   * redirecting into the full authenticated view). `PublicShareViewPage`
   * (the sole producer, via its own `isWrongAccount` prop) already swaps its
   * "sign in" prompts for "switch account" ones using that local prop, not
   * this context field — this is carried down for any masked component that
   * needs the same distinction without threading another prop through every
   * intermediate layer. Always `false` outside public mode / for a
   * genuinely anonymous visitor.
   */
  isViewerAccountMismatch?: boolean;
}

function noop(): void {
  // Default no-op for normal (non-public) mode — there's nothing to prompt.
}

const DEFAULT_PUBLIC_VIEW_VALUE: PublicViewValue = {
  isPublicView: false,
  grant: null,
  rosterPage: null,
  campaignDetails: null,
  shopifyCoupons: null,
  shopifyQuery: undefined,
  setShopifyQuery: undefined,
  shopifySearchInput: undefined,
  setShopifySearchInput: undefined,
  shopifyPageLoading: false,
  shopifyPageError: false,
  retryShopifyPage: undefined,
  campaignPlatform: undefined,
  campCode: '',
  tail: '',
  requestLogin: noop,
  isViewerAccountMismatch: false,
};

const PublicViewContext = createContext<PublicViewValue | null>(null);

export function PublicViewProvider(props: { value: PublicViewValue; children: ReactNode }): JSX.Element {
  const {
    isPublicView,
    grant,
    rosterPage,
    campaignDetails,
    shopifyCoupons,
    shopifyQuery,
    setShopifyQuery,
    shopifySearchInput,
    setShopifySearchInput,
    shopifyPageLoading,
    shopifyPageError,
    retryShopifyPage,
    campaignPlatform,
    campCode,
    tail,
    requestLogin,
    isViewerAccountMismatch,
  } = props.value;
  // Re-derive a stable object from the primitive/reference fields rather than
  // trusting the caller's `value` identity — every masked component consumes
  // this context, so an inline object literal at the call site would
  // otherwise re-render all of them on every parent render.
  const memoValue = useMemo<PublicViewValue>(
    () => ({
      isPublicView,
      grant,
      rosterPage,
      campaignDetails,
      shopifyCoupons,
      shopifyQuery,
      setShopifyQuery,
      shopifySearchInput,
      setShopifySearchInput,
      shopifyPageLoading,
      shopifyPageError,
      retryShopifyPage,
      campaignPlatform,
      campCode,
      tail,
      requestLogin,
      isViewerAccountMismatch,
    }),
    [
      isPublicView,
      grant,
      rosterPage,
      campaignDetails,
      shopifyCoupons,
      shopifyQuery,
      setShopifyQuery,
      shopifySearchInput,
      setShopifySearchInput,
      shopifyPageLoading,
      shopifyPageError,
      retryShopifyPage,
      campaignPlatform,
      campCode,
      tail,
      requestLogin,
      isViewerAccountMismatch,
    ],
  );

  return <PublicViewContext.Provider value={memoValue}>{props.children}</PublicViewContext.Provider>;
}

export function usePublicView(): PublicViewValue {
  const ctx = useContext(PublicViewContext);
  return ctx ?? DEFAULT_PUBLIC_VIEW_VALUE;
}
